/**
 * Typed API calls for the commune-admin console (`docs/ADMIN.md`).
 *
 * The dashboard, notes and members calls wrap already-generated
 * `api/schema.d.ts` endpoints (`docs/API.md` §9, shipped by L3/L6). The
 * other five wrap the endpoints `docs/ADMIN.md` §3 adds, not in the
 * generated schema yet: their payload shapes come from
 * `features/admin/types.ts` instead of `api/types.ts`, per that module's
 * own doc comment.
 */
import { apiFetch, apiFetchPostForBlob } from './client';

import type { CommuneDashboard, Note, Paginated } from './types';
import type {
  AdminNote,
  BuildingReferents,
  CommuneStreetCoverage,
  EngagementExportRequest,
  SessionMapEntry,
  AdminRegistrationCase,
} from '../features/admin/types';

/**
 * `GET /communes/{insee}/dashboard/`: KPIs and summaries for the Commune
 * screen (`docs/ADMIN.md` §2).
 *
 * @param insee - The commune's INSEE code.
 * @returns The dashboard payload.
 */
export function getCommuneDashboard(insee: string): Promise<CommuneDashboard> {
  return apiFetch<CommuneDashboard>(`communes/${encodeURIComponent(insee)}/dashboard/`);
}

/**
 * `GET /communes/{insee}/coverage/`: door coverage, by street
 * (`docs/ADMIN.md` §3.1).
 *
 * @param insee - The commune's INSEE code.
 * @returns Coverage counts and the per-street breakdown.
 */
export function getCommuneCoverage(insee: string): Promise<CommuneStreetCoverage> {
  return apiFetch<CommuneStreetCoverage>(`communes/${encodeURIComponent(insee)}/coverage/`);
}

/**
 * `GET /communes/{insee}/sessions/map/`: the commune's open sessions, each
 * with the start point it was opened from (`docs/ADMIN.md` §3.2).
 *
 * @param insee - The commune's INSEE code.
 * @returns One row per open session.
 */
export function getSessionsMap(insee: string): Promise<SessionMapEntry[]> {
  return apiFetch<SessionMapEntry[]>(`communes/${encodeURIComponent(insee)}/sessions/map/`);
}

/**
 * `GET /communes/{insee}/referents/`: building-by-building referent
 * coverage (`docs/ADMIN.md` §3.3).
 *
 * @param insee - The commune's INSEE code.
 * @returns Buildings, how many have a referent, and the per-building rows.
 */
export function getBuildingReferents(insee: string): Promise<BuildingReferents> {
  return apiFetch<BuildingReferents>(`communes/${encodeURIComponent(insee)}/referents/`);
}

/**
 * `GET /communes/{insee}/registration-cases/`: the Inscriptions & relances
 * table, open-first then oldest-first (`docs/ADMIN.md` §3.4).
 *
 * @param insee - The commune's INSEE code.
 * @returns One paginated page of cases.
 */
export function getRegistrationCases(insee: string): Promise<Paginated<AdminRegistrationCase>> {
  return apiFetch<Paginated<AdminRegistrationCase>>(
    `communes/${encodeURIComponent(insee)}/registration-cases/`,
  );
}

/** The `?flagged=` filter `getCommuneNotes` accepts. */
export type NotesFlaggedFilter = 'flagged' | 'unflagged' | 'all';

/**
 * `GET /communes/{insee}/notes/`: reviewable notes, flagged first
 * (`docs/API.md` §9, extended with `note_categories`/`note_reason` by
 * `docs/ADMIN.md` §3.5).
 *
 * @param insee - The commune's INSEE code.
 * @param filter - Restrict to flagged, to the rest, or leave unfiltered
 *   (`"all"`, the default).
 * @returns One paginated page of notes.
 */
export function getCommuneNotes(
  insee: string,
  filter: NotesFlaggedFilter = 'all',
): Promise<Paginated<AdminNote>> {
  const query = filter === 'flagged' ? '?flagged=1' : filter === 'unflagged' ? '?flagged=0' : '';
  return apiFetch<Paginated<AdminNote>>(`communes/${encodeURIComponent(insee)}/notes/${query}`);
}

/**
 * `POST /visits/{id}/purge-note/`: erase a flagged note's text
 * (`docs/API.md` §9).
 *
 * @param visitId - The visit carrying the note.
 * @returns The now-purged note row.
 */
export function purgeNote(visitId: number): Promise<Note> {
  return apiFetch<Note>(`visits/${visitId}/purge-note/`, { method: 'POST' });
}

/**
 * `POST /visits/{id}/keep-note/`: clear a note's flag without erasing it
 * (`docs/API.md` §9).
 *
 * @param visitId - The visit carrying the note.
 * @returns The now-unflagged note row.
 */
export function keepNote(visitId: number): Promise<Note> {
  return apiFetch<Note>(`visits/${visitId}/keep-note/`, { method: 'POST' });
}

/**
 * `POST /communes/{insee}/engagements/export/`: the engagements CSV export
 * (`docs/ADMIN.md` §3.6). The caller is responsible for turning the blob
 * into a download — see `features/admin/pages/EngagementsPage.tsx`.
 *
 * @param insee - The commune's INSEE code.
 * @param request - The kind to export, and whether to skip already-exported rows.
 * @returns The CSV file's bytes.
 */
export function exportEngagements(insee: string, request: EngagementExportRequest): Promise<Blob> {
  return apiFetchPostForBlob(`communes/${encodeURIComponent(insee)}/engagements/export/`, request);
}
